import { useMemo, useState, type ReactElement } from 'react';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { TestRunEditDialog } from '@/features/executions/components/TestRunEditDialog';
import { useTestRunEditDialog } from '@/features/executions/components/useTestRunEditDialog';
import { useTestRunsTableRowActions } from '@/features/executions/components/useTestRunsTableRowActions';
import { EditFeatureDialog } from '@/features/features/components/dialogs';
import { useFeatureEditDialog } from '@/features/features/components/useFeatureEditDialog';
import { useFeaturesTableRowActions } from '@/features/features/components/useFeaturesTableRowActions';
import { EditReleaseDialog } from '@/features/releases/components/dialogs';
import { ReleaseFeaturesNestedTable } from '@/features/releases/components/ReleaseFeaturesNestedTable';
import { useReleaseEditDialog } from '@/features/releases/components/useReleaseEditDialog';
import { useReleasesTableRowActions } from '@/features/releases/components/useReleasesTableRowActions';
import { resolveGenericStatusCardIcon } from '@/features/shared/components/chartPanels/StackedMetricsStatusChartSection.icons';
import { TableActionsCell } from '@/features/shared/components/tableSystem/TableCells';
import { tableV2NestedCarrierRowSx } from '@/features/shared/components/tableSystem/TableV2NestedTable';
import {
  TableV2ActionsHeaderIcon,
  TableV2ChevronButton,
  TableV2DateCell,
  TableV2HeaderLabel,
  TableV2NameCell,
  TableV2PassRateCell,
  TableV2ProjectCell,
  TableV2SortableHeader,
  TableV2StatusChip,
  TableV2TagGroup,
  tableV2ActionsColumnWidth,
  tableV2BodyRowHeightPx,
  tableV2ChevronCellSx,
  tableV2ChevronColumnWidth,
} from '@/features/shared/components/tableSystem/TableV2Primitives';
import { TableV2QueryShell } from '@/features/shared/components/tableSystem/TableV2QueryShell';
import { buildTagColorByName } from '@/features/shared/tags';
import { featureStatusOptions, releaseStatusOptions } from '@/shared/constants/statusOptions';
import { getEntityActionLabel } from '@/shared/copy/actions';
import { getDialogMessage } from '@/shared/copy/dialogs';
import { r, s } from '@/theme/tokens';
import { uiTokens } from '@/theme/uiTokens';
import { Box, CursorTooltip, TableCell, TableRow, Text } from '@/ui/primitives';
import type { FeatureTestRunActions } from '@/features/features/components/FeatureTestRunsNestedTable';
import type { TableV2SkeletonColumnKind } from '@/features/shared/components/tableSystem/TableV2Primitives';
import type { TagColor, TagOption } from '@/shared/data/tags';
import type { ApiSort, GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type { Theme } from '@/ui/primitives';
import type { FeatureDto, ProjectDto, QueryResult, ReleaseListItemDto } from '@flowqa/shared';
import type { QueryKey } from '@tanstack/react-query';

type ReleasesTableV2Props = {
  active?: boolean;
  fixedFilters?: GridQueryInput['filters'];
  noRowsText: string;
  projectOptions?: Array<
    Pick<ProjectDto, 'badgeChars' | 'badgeColor' | 'badgeEnabled' | 'id' | 'name'>
  >;
  queryFn: (input: GridQueryInput) => Promise<QueryResult<ReleaseListItemDto>>;
  queryKey: QueryKey;
  search?: string;
  selectedProjectId: string | null;
  sort: ApiSort;
  tagOptionsOverride?: TagOption[];
  onSortChange: (sort: ApiSort) => void;
};

type SortableReleaseField = 'name' | 'plannedFor' | 'releasedAt' | 'status';

const releaseSkeletonColumns = [
  'chevron',
  'name',
  'status',
  'dateTime',
  'dateTime',
  'passRate',
  'passRate',
  'tags',
  'actions',
] as const;

const buildReleaseColgroup = (showProjectColumn: boolean): ReactElement => (
  <colgroup>
    <col width={tableV2ChevronColumnWidth} />
    <col width={showProjectColumn ? '14%' : '16%'} />
    <col width={showProjectColumn ? '8%' : '9%'} />
    <col width={showProjectColumn ? '11%' : '12%'} />
    <col width={showProjectColumn ? '11%' : '12%'} />
    <col width={showProjectColumn ? '15%' : '17%'} />
    <col width={showProjectColumn ? '15%' : '17%'} />
    <col width={showProjectColumn ? '10%' : '13%'} />
    {showProjectColumn ? <col width="10%" /> : null}
    <col width={tableV2ActionsColumnWidth} />
  </colgroup>
);

const buildReleaseSkeletonColumns = (showProjectColumn: boolean): TableV2SkeletonColumnKind[] => [
  ...releaseSkeletonColumns.slice(0, 8),
  ...(showProjectColumn ? (['project'] as const) : []),
  ...releaseSkeletonColumns.slice(8),
];

type FeatureStatusKey = 'draft' | 'planned' | 'ready' | 'released';

type FeatureStatusSegment = {
  key: FeatureStatusKey;
  label: string;
  count: number;
  color: string;
};

type FeatureStatusBreakdown = {
  total: number;
  readyOrReleased: number;
  segments: FeatureStatusSegment[];
};

const formatStatusPercent = (count: number, total: number): string => {
  if (total <= 0) return '0%';
  const value = Math.round((count / total) * 1000) / 10;
  return Number.isInteger(value) ? `${String(value)}%` : `${value.toFixed(1)}%`;
};

const featureStatusTooltipTextSx = {
  fontSize: (theme: Theme) => theme.typography.pxToRem(15),
} as const;

const buildFeatureStatusBreakdown = (row: ReleaseListItemDto): FeatureStatusBreakdown => {
  const counts = (row.features?.items ?? []).reduce<Record<FeatureStatusKey, number>>(
    (accumulator, feature) => {
      accumulator[feature.status] += 1;
      return accumulator;
    },
    { draft: 0, planned: 0, ready: 0, released: 0 },
  );
  const total = counts.draft + counts.planned + counts.ready + counts.released;
  const readyOrReleased = counts.ready + counts.released;

  return {
    total,
    readyOrReleased,
    segments: [
      {
        key: 'draft',
        label: 'Drafted',
        count: counts.draft,
        color: uiTokens.statusColors.neutral,
      },
      {
        key: 'planned',
        label: 'Planned',
        count: counts.planned,
        color: uiTokens.statusColors.blue,
      },
      {
        key: 'ready',
        label: 'Ready',
        count: counts.ready,
        color: uiTokens.statusColors.orange,
      },
      {
        key: 'released',
        label: 'Released',
        count: counts.released,
        color: uiTokens.statusColors.green,
      },
    ],
  };
};

const FeatureStatusCell = ({ row }: { row: ReleaseListItemDto }): ReactElement => {
  const breakdown = buildFeatureStatusBreakdown(row);
  const visibleSegments = breakdown.segments.filter((segment) => segment.count > 0);

  if (breakdown.total <= 0) {
    return (
      <Text role="body" color="textSecondary" component="span">
        -
      </Text>
    );
  }

  return (
    <Box sx={{ minWidth: s('3xl') }}>
      <Box sx={{ alignItems: 'baseline', display: 'flex', gap: s('sm') }}>
        <Text role="body" component="span">
          {formatStatusPercent(breakdown.readyOrReleased, breakdown.total)}
        </Text>
        <Text role="captionSmall" color="textSecondary" component="span">
          ({String(breakdown.readyOrReleased)}/{String(breakdown.total)})
        </Text>
      </Box>
      <CursorTooltip
        title={
          <Box sx={{ display: 'grid', minWidth: '180px', py: s('xs'), rowGap: s('xxs') }}>
            {visibleSegments.map((segment) => {
              const StatusIcon = resolveGenericStatusCardIcon(segment.key);
              return (
                <Box
                  key={segment.key}
                  sx={{
                    alignItems: 'center',
                    display: 'grid',
                    gridTemplateColumns: `max-content ${s('md')} minmax(0, 1fr) ${s('xs')} max-content max-content`,
                  }}
                >
                  <StatusIcon
                    aria-hidden="true"
                    fontSize="small"
                    sx={{
                      color: segment.color,
                      flex: '0 0 auto',
                      height: '1em',
                      width: '1em',
                    }}
                  />
                  <Box component="span" aria-hidden="true" />
                  <Text
                    role="body"
                    component="span"
                    color="inherit"
                    sx={featureStatusTooltipTextSx}
                  >
                    {segment.label}
                  </Text>
                  <Box component="span" aria-hidden="true" />
                  <Text
                    role="body"
                    component="span"
                    color="inherit"
                    sx={[
                      featureStatusTooltipTextSx,
                      {
                        fontVariantNumeric: 'tabular-nums',
                        minWidth: '3ch',
                        textAlign: 'right',
                        whiteSpace: 'nowrap',
                      },
                    ]}
                  >
                    {segment.count.toLocaleString()}
                  </Text>
                  <Text
                    role="body"
                    component="span"
                    color="inherit"
                    sx={[
                      featureStatusTooltipTextSx,
                      {
                        fontVariantNumeric: 'tabular-nums',
                        ml: s('md'),
                        minWidth: '6ch',
                        textAlign: 'right',
                        whiteSpace: 'nowrap',
                      },
                    ]}
                  >
                    ({formatStatusPercent(segment.count, breakdown.total)})
                  </Text>
                </Box>
              );
            })}
          </Box>
        }
        disableHoverListener={visibleSegments.length === 0}
        arrow
        slotProps={{ tooltip: { sx: { px: s('md'), py: s('sm'), width: 'fit-content' } } }}
      >
        <Box
          aria-label="Feature status breakdown"
          role="img"
          sx={{
            backgroundColor: (theme) => theme.app.state.disabledBg,
            borderRadius: r('pill'),
            display: 'flex',
            height: s('sm'),
            mt: s('sm'),
            overflow: 'hidden',
            width: '100%',
          }}
        >
          {visibleSegments.map((segment) => (
            <Box
              key={segment.key}
              sx={{
                backgroundColor: segment.color,
                height: '100%',
                width: `${String((segment.count / breakdown.total) * 100)}%`,
              }}
            />
          ))}
        </Box>
      </CursorTooltip>
    </Box>
  );
};

const ReleaseRow = ({
  deleteRowPendingId,
  featureDeleteRowPendingId,
  featureUpdateRowTagsColorPendingId,
  isLastVisibleRow,
  row,
  rowProject,
  showProjectColumn,
  tagOptions,
  testRunActions,
  updateRowTagsColorPendingId,
  onDeleteRow,
  onDeleteFeatureRow,
  onEditRow,
  onEditFeatureRow,
  onUpdateFeatureRowTagsColor,
  onUpdateRowTagsColor,
}: {
  deleteRowPendingId: string | null;
  featureDeleteRowPendingId: string | null;
  featureUpdateRowTagsColorPendingId: string | null;
  isLastVisibleRow: boolean;
  row: ReleaseListItemDto;
  rowProject: Pick<ProjectDto, 'badgeChars' | 'badgeColor' | 'badgeEnabled' | 'name'> | null;
  showProjectColumn: boolean;
  tagOptions: TagOption[];
  testRunActions: FeatureTestRunActions;
  updateRowTagsColorPendingId: string | null;
  onDeleteRow: (row: ReleaseListItemDto) => void;
  onDeleteFeatureRow: (row: FeatureDto) => void;
  onEditRow: (row: ReleaseListItemDto) => void;
  onEditFeatureRow: (row: FeatureDto) => void;
  onUpdateFeatureRowTagsColor: (input: {
    row: FeatureDto;
    tags: string[];
    colors: TagColor[];
  }) => void;
  onUpdateRowTagsColor: (input: {
    row: ReleaseListItemDto;
    tags: string[];
    colors: TagColor[];
  }) => void;
}): ReactElement => {
  const [expanded, setExpanded] = useState(false);
  const tagColorByName = buildTagColorByName(tagOptions);
  const features = row.features?.items ?? [];
  const testCounts = {
    total:
      (row.latestTestResults?.counts?.passed ?? 0) +
      (row.latestTestResults?.counts?.failed ?? 0) +
      (row.latestTestResults?.counts?.errored ?? 0) +
      (row.latestTestResults?.counts?.skipped ?? 0) +
      (row.latestTestResults?.counts?.untested ?? 0),
    passed: row.latestTestResults?.counts?.passed ?? 0,
    failed: row.latestTestResults?.counts?.failed ?? 0,
    errored: row.latestTestResults?.counts?.errored ?? 0,
    skipped: row.latestTestResults?.counts?.skipped ?? 0,
    untested: row.latestTestResults?.counts?.untested ?? 0,
  };

  return (
    <>
      <TableRow
        sx={{
          backgroundColor: 'background.paper',
          '& > td': {
            borderBottom:
              expanded || isLastVisibleRow ? 0 : (theme) => `1px solid ${theme.app.colors.border}`,
            height: `${String(tableV2BodyRowHeightPx)}px`,
            py: s('md'),
            verticalAlign: 'middle',
          },
        }}
      >
        <TableCell align="center" sx={tableV2ChevronCellSx}>
          <TableV2ChevronButton
            expanded={expanded}
            label={expanded ? 'Collapse release features' : 'Expand release features'}
            onClick={() => {
              setExpanded((current) => !current);
            }}
          />
        </TableCell>
        <TableCell>
          <TableV2NameCell href={`/releases/${row.slug || row.id}`} primary={row.name} />
        </TableCell>
        <TableCell>
          <TableV2StatusChip status={row.status} />
        </TableCell>
        <TableCell>
          <TableV2DateCell value={row.plannedFor ?? null} />
        </TableCell>
        <TableCell>
          <TableV2DateCell value={row.releasedAt ?? null} />
        </TableCell>
        <TableCell>
          <FeatureStatusCell row={row} />
        </TableCell>
        <TableCell>
          <TableV2PassRateCell counts={testCounts} />
        </TableCell>
        <TableCell>
          <TableV2TagGroup tagColorByName={tagColorByName} tags={row.tags ?? []} />
        </TableCell>
        {showProjectColumn ? (
          <TableCell>
            <TableV2ProjectCell project={rowProject} fallbackName={row.project?.name} />
          </TableCell>
        ) : null}
        <TableCell align="center" sx={{ p: s('none'), width: tableV2ActionsColumnWidth }}>
          <TableActionsCell
            rowName={row.name}
            verticalAlign="center"
            editActionLabel={getEntityActionLabel('edit', 'Release')}
            editTagsEntityNoun="release"
            rowTags={row.tags ?? []}
            onEdit={() => {
              onEditRow(row);
            }}
            onUpdateTagsColor={(input) => {
              onUpdateRowTagsColor({ row, ...input });
            }}
            updateTagsColorLoading={updateRowTagsColorPendingId === row.id}
            deleteActionLabel={getEntityActionLabel('delete', 'Release')}
            onDelete={() => {
              onDeleteRow(row);
            }}
            disableDelete={deleteRowPendingId === row.id}
          />
        </TableCell>
      </TableRow>
      <TableRow
        sx={[
          tableV2NestedCarrierRowSx,
          expanded && !isLastVisibleRow
            ? {
                '& > td': {
                  borderBottom: (theme) => `1px solid ${theme.app.colors.border}`,
                },
              }
            : {},
        ]}
      >
        <TableCell colSpan={buildReleaseSkeletonColumns(showProjectColumn).length}>
          <ReleaseFeaturesNestedTable
            deleteRowPendingId={featureDeleteRowPendingId}
            expanded={expanded}
            features={features}
            projectId={row.projectId}
            tagColorByName={tagColorByName}
            testRunActions={testRunActions}
            updateRowTagsColorPendingId={featureUpdateRowTagsColorPendingId}
            onDeleteRow={onDeleteFeatureRow}
            onEditRow={onEditFeatureRow}
            onUpdateRowTagsColor={onUpdateFeatureRowTagsColor}
          />
        </TableCell>
      </TableRow>
    </>
  );
};

export const ReleasesTableV2 = ({
  active = true,
  fixedFilters,
  noRowsText,
  projectOptions = [],
  queryFn,
  queryKey,
  search,
  selectedProjectId,
  sort,
  tagOptionsOverride = [],
  onSortChange,
}: ReleasesTableV2Props): ReactElement => {
  const showProjectColumn = selectedProjectId === null;
  const skeletonColumns = buildReleaseSkeletonColumns(showProjectColumn);
  const projectById = useMemo(
    () => new Map(projectOptions.map((project) => [project.id, project])),
    [projectOptions],
  );
  const { editErrors, editForm, editSaving, editTarget, onEditClose, onEditRow, onEditSave } =
    useReleaseEditDialog();
  const {
    editErrors: featureEditErrors,
    editForm: featureEditForm,
    editReleaseOptions: featureEditReleaseOptions,
    editSaving: featureEditSaving,
    editTarget: featureEditTarget,
    onEditClose: onFeatureEditClose,
    onEditRow: onEditFeatureRow,
    onEditSave: onFeatureEditSave,
  } = useFeatureEditDialog();
  const {
    editLoading: testRunEditLoading,
    editTarget: testRunEditTarget,
    onEditClose: onTestRunEditClose,
    onEditRow: onEditTestRunRow,
    onEditSave: onTestRunEditSave,
  } = useTestRunEditDialog(selectedProjectId);
  const {
    deleteLoading,
    deleteRowPendingId,
    deleteTarget,
    onDeleteClose,
    onDeleteConfirm,
    onDeleteRow,
    onUpdateRowTagsColor,
    updateRowTagsColorPendingId,
  } = useReleasesTableRowActions({ selectedProjectId });
  const {
    deleteLoading: featureDeleteLoading,
    deleteRowPendingId: featureDeleteRowPendingId,
    deleteTarget: featureDeleteTarget,
    onDeleteClose: onFeatureDeleteClose,
    onDeleteConfirm: onFeatureDeleteConfirm,
    onDeleteRow: onDeleteFeatureRow,
    onUpdateRowTagsColor: onUpdateFeatureRowTagsColor,
    updateRowTagsColorPendingId: featureUpdateRowTagsColorPendingId,
  } = useFeaturesTableRowActions({ selectedProjectId });
  const testRunRowActions = useTestRunsTableRowActions({ selectedProjectId });
  const testRunActions = {
    ...testRunRowActions,
    onEditRow: onEditTestRunRow,
  };

  const handleSortChange = (field: SortableReleaseField): void => {
    onSortChange({
      field,
      direction: sort.field === field && sort.direction === 'desc' ? 'asc' : 'desc',
    });
  };

  const header = (
    <TableRow>
      <TableCell sx={{ p: s('none'), width: tableV2ChevronColumnWidth }} />
      <TableCell>
        <TableV2SortableHeader
          activeSort={sort}
          field="name"
          label="Release"
          onSortChange={handleSortChange}
        />
      </TableCell>
      <TableCell>
        <TableV2SortableHeader
          activeSort={sort}
          field="status"
          label="Status"
          onSortChange={handleSortChange}
        />
      </TableCell>
      <TableCell>
        <TableV2SortableHeader
          activeSort={sort}
          field="plannedFor"
          label="Planned For"
          onSortChange={handleSortChange}
        />
      </TableCell>
      <TableCell>
        <TableV2SortableHeader
          activeSort={sort}
          field="releasedAt"
          label="Released At"
          onSortChange={handleSortChange}
        />
      </TableCell>
      <TableCell>
        <TableV2HeaderLabel label="Features Status" />
      </TableCell>
      <TableCell>
        <TableV2HeaderLabel label="Testing Status" />
      </TableCell>
      <TableCell>
        <TableV2HeaderLabel label="Tags" />
      </TableCell>
      {showProjectColumn ? (
        <TableCell>
          <TableV2HeaderLabel label="Project" />
        </TableCell>
      ) : null}
      <TableCell align="center" sx={{ p: s('none'), width: tableV2ActionsColumnWidth }}>
        <TableV2ActionsHeaderIcon />
      </TableCell>
    </TableRow>
  );

  return (
    <>
      <TableV2QueryShell
        active={active}
        ariaLabel="Releases"
        colgroup={buildReleaseColgroup(showProjectColumn)}
        emptyText={noRowsText}
        entityLabel="releases"
        fixedFilters={fixedFilters}
        header={header}
        queryFn={queryFn}
        queryKey={queryKey}
        search={search}
        skeletonColumns={skeletonColumns}
        sorts={[sort]}
        renderRow={(row, context) => (
          <ReleaseRow
            key={row.id}
            deleteRowPendingId={deleteRowPendingId}
            featureDeleteRowPendingId={featureDeleteRowPendingId}
            featureUpdateRowTagsColorPendingId={featureUpdateRowTagsColorPendingId}
            isLastVisibleRow={context.isLastVisibleRow}
            row={row}
            rowProject={
              projectById.get(row.projectId) ??
              (row.project
                ? {
                    name: row.project.name,
                    badgeChars: '',
                    badgeColor: 'neutral',
                    badgeEnabled: false,
                  }
                : null)
            }
            showProjectColumn={showProjectColumn}
            tagOptions={tagOptionsOverride}
            testRunActions={testRunActions}
            updateRowTagsColorPendingId={updateRowTagsColorPendingId}
            onDeleteRow={onDeleteRow}
            onDeleteFeatureRow={onDeleteFeatureRow}
            onEditRow={onEditRow}
            onEditFeatureRow={onEditFeatureRow}
            onUpdateFeatureRowTagsColor={onUpdateFeatureRowTagsColor}
            onUpdateRowTagsColor={onUpdateRowTagsColor}
          />
        )}
      />
      <EditReleaseDialog
        open={editTarget !== null}
        saving={editSaving}
        values={editForm.values}
        errors={{ name: editErrors.fieldErrorProps('name') }}
        statusOptions={releaseStatusOptions}
        showStatusSelect
        errorMessage={editForm.submitError}
        onClose={onEditClose}
        onSave={onEditSave}
        handlers={{
          onNameChange: (value) => {
            editForm.setField('name', value);
          },
          onStatusChange: (value) => {
            editForm.setField('status', value);
          },
          onPlannedForChange: (value) => {
            editForm.setField('plannedFor', value);
          },
          onReleasedAtChange: (value) => {
            editForm.setField('releasedAt', value);
          },
          onTagsChange: (value) => {
            editForm.setField('tags', value);
          },
        }}
      />
      <EditFeatureDialog
        open={featureEditTarget !== null}
        saving={featureEditSaving}
        values={featureEditForm.values}
        errors={{ name: featureEditErrors.fieldErrorProps('name') }}
        statusOptions={featureStatusOptions}
        releaseOptions={featureEditReleaseOptions}
        errorMessage={featureEditForm.submitError}
        onClose={onFeatureEditClose}
        onSave={onFeatureEditSave}
        handlers={{
          onNameChange: (value) => {
            featureEditForm.setField('name', value);
          },
          onStatusChange: (value) => {
            featureEditForm.setField('status', value);
          },
          onReleaseChange: (value) => {
            featureEditForm.setField('releaseId', value);
          },
          onPlannedForChange: (value) => {
            featureEditForm.setField('plannedFor', value);
          },
          onReleasedAtChange: (value) => {
            featureEditForm.setField('releasedAt', value);
          },
          onTagsChange: (value) => {
            featureEditForm.setField('tags', value);
          },
        }}
      />
      <TestRunEditDialog
        open={testRunEditTarget !== null}
        execution={testRunEditTarget}
        loading={testRunEditLoading}
        onClose={onTestRunEditClose}
        onSave={onTestRunEditSave}
      />
      <ConfirmDialog
        open={deleteTarget !== null}
        title={getDialogMessage('destructive.delete.title', { entity: 'release' })}
        description={getDialogMessage('destructive.delete.simpleDescription', {
          entity: 'release',
        })}
        confirmLabel={getDialogMessage('destructive.delete.confirmLabel', {
          entity: 'release',
        })}
        confirmColor="error"
        loading={deleteLoading}
        onClose={onDeleteClose}
        onConfirm={onDeleteConfirm}
      />
      <ConfirmDialog
        open={featureDeleteTarget !== null}
        title={getDialogMessage('destructive.delete.title', { entity: 'feature' })}
        description={getDialogMessage('destructive.delete.simpleDescription', {
          entity: 'feature',
        })}
        confirmLabel={getDialogMessage('destructive.delete.confirmLabel', {
          entity: 'feature',
        })}
        confirmColor="error"
        loading={featureDeleteLoading}
        onClose={onFeatureDeleteClose}
        onConfirm={onFeatureDeleteConfirm}
      />
      <ConfirmDialog
        open={testRunRowActions.deleteTarget !== null}
        title={getDialogMessage('destructive.delete.title', { entity: 'testRun' })}
        description={getDialogMessage('destructive.delete.simpleDescription', {
          entity: 'testRun',
        })}
        confirmLabel={getDialogMessage('destructive.delete.confirmLabel', {
          entity: 'testRun',
        })}
        confirmColor="error"
        loading={testRunRowActions.deleteLoading}
        onClose={testRunRowActions.onDeleteClose}
        onConfirm={testRunRowActions.onDeleteConfirm}
      />
    </>
  );
};
